import { useQuery } from "@tanstack/react-query";
import { AlertTriangle } from "@untitledui/icons";
import { handleError } from "@/api/error";
import { client, useScopedMerchantClient } from "@/api/scoped-merchant-client";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

const useExpiredCollaborations = (videoId: string) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["expired-collaborations", scope.merchantId, scope.workspaceId, videoId],
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].assurance.video[":videoId"][
            "expired-collaborations"
          ].$get({
            param: { ...scope.param, videoId },
          })
        : await client.merchant[":merchantId"].assurance.video[":videoId"]["expired-collaborations"].$get({
            param: { ...scope.param, videoId },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId && !!videoId,
  });
};

export const CollaborationExpiredBadge = ({ videoId }: { videoId: string }) => {
  const { data } = useExpiredCollaborations(videoId);

  if (!data || data.length === 0) return null;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge className="bg-warning-primary text-warning-primary hover:bg-warning-secondary border-fg-warning-secondary cursor-default">
          <AlertTriangle className="w-3 h-3 mr-1" />
          Collaboration Expired
        </Badge>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-64">
        <p className="font-medium text-xs mb-1">Expired collaborations:</p>
        <ul className="text-xs space-y-0.5">
          {data.map((collab) => (
            <li key={`${collab.type}-${collab.id}`}>• {collab.name}</li>
          ))}
        </ul>
      </TooltipContent>
    </Tooltip>
  );
};
